import { OfferMessage } from '@atomicfinance/bitcoin-dlc-provider';
import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from '@chakra-ui/core';
import BigNumber from 'bignumber.js';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import SadFaceImage from '../../assets/sad_face.svg';
import StepSidebar from '../../components/StepSidebar';
import WalletUnlockModal from '../../components/WalletUnlockModal';
import { useAsyncInterval } from '../../hooks/useAsyncInterval';
import { usePersistBet } from '../../hooks/usePersistBet';
import { Bitcoin, createClient } from '../../services/chainClient';
import Tower, { getTowerService } from '../../services/towerService';
import { useAppDispatch } from '../../store';
import { RootState } from '../../store/reducers';
import { setClientInitialized } from '../../store/wallet/walletSlice';
import { BetDetails } from '../../types/BetInterfaces';
import BetConfirmation from '../Bet/BetConfirmation';
import BetDeposit from '../Bet/BetDeposit';
import BetOverview from '../Bet/BetOverview';
import WalletInfo from '../Wallet/WalletInfo';
import WalletPassphrase from '../Wallet/WalletPassphrase';
import WalletPasswordSetup from '../Wallet/WalletPasswordSetup';
import BetLink from './BetLink';

const steps = [
  'Bet Type',
  'Bet Amount & Odds',
  'Bet Overview',
  'Wallet Set-up',
  'Wallet Set-up',
  'Wallet Set-up',
  'Wallet Set-up',
  'Deposit Bet Amount',
  'Deposit Bet Amount',
  'Bet Finalized',
  'Finalizing Bet',
  'Bet Accepted',
];

const SIDEBAR_STEPS = {
  'Wallet Set-up': localStorage.getItem('encryptedMnemonic') ? 3 : 4,
  'Deposit Amount': 7,
  'Bet Acceptance': 10,
};

export enum CounterpartyOfferSteps {
  VIEW = 1,
  WALLET_INFO = 4,
  WALLET_PASSWORD_SETUP,
  WALLET_PASSPHRASE,
  BET_DEPOSIT,
}

const StepExtraInfo = {
  5: {
    title: 'Why create a wallet password?',
    info:
      'Creating a password provides extra layer of security with your funds while allowing you to conveniently access your bets in future.',
  },
  6: {
    title: 'Access your bets on any browser?',
    info:
      'In the event where you login to Atomic Odds in a different browser or computer, you can always recover your bets and account by inputting the seed phrase when logging into the platform.',
  },
  7: {
    title: 'We will be charging 5% fees to winner of the bet',
    info: `At Atomic.Finance we're here to build products that are sustainable over the long run. This fee will go a long way towards helping us maintain and improve Atomic Odds over time.`,
  },
};

const AcceptBet = ({
  betDetails,
  offer,
  setAccepted,
  name,
}: {
  betDetails: BetDetails;
  offer: OfferMessage;
  setAccepted: (accepted: boolean) => void;
  name: string;
}) => {
  const mnemonic = useSelector((state: RootState) => state.wallet.mnemonic);

  const [step, setStep] = useState(4);
  const tower = useRef<Tower | null>(null);
  const bitcoinRef = useRef<Bitcoin | null>(null);

  const [staticPubkey, setStaticPubkey] = useState('');

  // Track completion of DLC creation
  const [dlcSetup, setDlcSetup] = useState(false);
  const [published, setPublished] = useState(false);

  const [isTaken, setIsTaken] = useState(false);

  const {
    isOpen: isBetDetailsOpen,
    onOpen: onBetDetailsOpen,
    onClose: onBetDetailsClose,
  } = useDisclosure();

  const dispatch = useAppDispatch();

  const persistBet = usePersistBet(mnemonic);

  // check if offer is still available.
  useAsyncInterval(async () => {
    if (!tower.current) return;
    const { contractStage } = await tower.current?.getOffer(offer.contractId);

    if (contractStage !== 'OFFER' && step < 10) {
      setIsTaken(true);
    }
  }, 10000);

  useEffect(() => {
    if (!mnemonic) return;
    if (localStorage.getItem('encryptedMnemonic') && step === 4) {
      setStep(7);
    }
    (async () => {
      const bitcoin = createClient(mnemonic);
      bitcoinRef.current = bitcoin;

      dispatch(setClientInitialized(true));

      console.log(bitcoin);

      const towerInstance = getTowerService(bitcoin);
      await towerInstance.initialize();

      tower.current = towerInstance;

      const staticPubkeyAddress = await bitcoin.getMethod('getAddresses')(
        0,
        2,
        false
      );

      const staticPubkeyPair = await bitcoin.getMethod('keyPair')(
        staticPubkeyAddress[1].derivationPath
      );

      console.log(
        'Your static public key:',
        staticPubkeyPair.publicKey.toString('hex')
      );
      setStaticPubkey(staticPubkeyPair.publicKey.toString('hex'));
    })();
  }, [dispatch, mnemonic, step]);

  const history = useHistory();

  const onDeposit = async ({ inputs }) => {
    console.log('onDeposit Called');
    if (!bitcoinRef.current) throw new Error('Bitcoin client not initialized');
    setStep(10);

    console.log(inputs);

    const startingIndex = ((await tower.current?.getNumContracts(staticPubkey)) || 0) * 2;
    inputs = inputs.map(
      (input) =>
        (input.amount = {
          ...input,
          amount: new BigNumber(input.amount).times(1e8).toNumber(),
        })
    );
    console.log('offer', offer);
    console.log('startingIndex', startingIndex);
    console.log('inputs', inputs);

    const acceptMessage = await bitcoinRef.current.confirmContractOffer(
      offer,
      startingIndex,
      inputs
    );
    setDlcSetup(true);
    console.log(acceptMessage);
    console.log(
      'POSTED ACCEPT',
      await tower.current?.postAccept(
        acceptMessage,
        offer.localPartyInputs.fundPublicKey,
        startingIndex
      )
    );

    persistBet(acceptMessage.contractId, betDetails, 'ACCEPT');
    setPublished(true);
  };

  if (!mnemonic) return <WalletUnlockModal />;

  const depositComponent = (
    <BetDeposit
      onBack={() => {}}
      onDeposit={onDeposit}
      betAmount={betDetails.betAmount}
    />
  );

  return (
    <>
      <Modal isOpen={isTaken} onClose={() => {}} size="xl" isCentered>
        <ModalOverlay>
          <ModalContent textAlign="center" p={4}>
            <ModalHeader>Bet Offer No Longer Available</ModalHeader>
            <ModalBody textAlign="center">
              <Image src={SadFaceImage} mx="auto" mb={4} />
              <Text fontSize="sm">
                Looks like someone else took the bet offer before you.
              </Text>
              <br />
              <Button onClick={() => history.push('/offers/jimmysong')}>
                View Other Bet Offers
              </Button>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      </Modal>

      <Modal
        isOpen={isBetDetailsOpen}
        onClose={onBetDetailsClose}
        size="xl"
        isCentered
      >
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>Bet Details</ModalHeader>
            <ModalCloseButton onClick={onBetDetailsClose} />
            <ModalBody pb="1rem">
              <BetOverview
                onBack={() => {}}
                onNext={() => {}}
                prediction={betDetails.prediction}
                betAmount={betDetails.betAmount}
                betSize={betDetails.betSize}
                showButtons={false}
              />
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      </Modal>
      <Flex h="100%" position="relative">
        <Button
          bg="white"
          color="blue.600"
          borderColor="blue.600"
          borderWidth="1px"
          position="absolute"
          top="5"
          right="5"
          size="sm"
          colorScheme="white"
          onClick={() => setAccepted(false)}
        >
          Exit Bet Process
        </Button>
        <StepSidebar steps={SIDEBAR_STEPS} step={step} />

        <Flex
          flex={1}
          flexDir="column"
          alignItems="center"
          maxW="45em"
          m="auto"
          justifyContent="center"
        >
          <Flex alignItems="center" w="full">
            <Heading mb={4} textTransform="uppercase">
              {steps[step]}
            </Heading>

            <Text
              ml="auto"
              cursor="pointer"
              textDecoration="underline"
              onClick={onBetDetailsOpen}
            >
              View Bet Details
            </Text>
          </Flex>

          {StepExtraInfo[step] && (
            <Flex
              p={2}
              flexDir="column"
              w="full"
              bg="#646883"
              color="white"
              borderRadius="4px"
              mb={4}
              fontSize="xs"
            >
              {StepExtraInfo[step].title && (
                <Text fontWeight="500">{StepExtraInfo[step].title}</Text>
              )}
              <Text>{StepExtraInfo[step].info}</Text>
            </Flex>
          )}

          <Box
            width="full"
            backgroundColor="white"
            boxShadow="0px 0px 14px 3px rgba(0, 0, 0, 0.07)"
            borderRadius="4px"
            p="2rem"
          >
            <TransitionGroup exit={false}>
              <CSSTransition key={step} timeout={500} classNames="fade">
                <Box key={step}>
                  {(function () {
                    switch (step) {
                      case 4:
                        return localStorage.getItem('encryptedMnemonic') ? (
                          depositComponent
                        ) : (
                          <WalletInfo
                            onBack={() => setAccepted(false)}
                            onNext={() => setStep(5)}
                          />
                        );
                      case 5:
                        return (
                          <WalletPasswordSetup
                            onBack={() => setStep(4)}
                            onNext={() => setStep(6)}
                          />
                        );
                      case 6:
                        return (
                          <WalletPassphrase
                            onBack={() => setStep(5)}
                            onNext={() => setStep(7)}
                          />
                        );
                      case 7:
                        return (
                          betDetails && bitcoinRef.current && depositComponent
                        );
                      case 10:
                        return (
                          <BetConfirmation
                            confirmations={[
                              {
                                label: 'Generating Acceptance',
                                completeLabel: 'Acceptance Generated',
                                complete: dlcSetup,
                              },
                              {
                                label: 'Publishing Acceptance',
                                completeLabel: 'Acceptance Published',
                                complete: published,
                              },
                            ]}
                            onNext={() => setStep(11)}
                          />
                        );
                      case 11:
                        return (
                          <BetLink
                            onNext={() => history.push('/dashboard')}
                            name={name}
                            betId={offer.contractId}
                            staticPubkey={staticPubkey}
                          />
                        );

                      default:
                        return <p>Error</p>;
                    }
                  })()}
                </Box>
              </CSSTransition>
            </TransitionGroup>
          </Box>
        </Flex>
      </Flex>
    </>
  );
};

export default AcceptBet;
